import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Modal, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SecondaryTab } from 'components/Tabs';
import PersonalInfo from '../PersonalInfo/Info';
import Settings from '../Settings';
import Button from 'components/Button';
import { get, put, post, upload } from 'api';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import { SelectInput, TextInput } from 'components/Inputs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Cities } from 'constants/Selections';

const ContentView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(t('personalInfo'));
  const [userInfo, setUserInfo] = useState([]);
  const [avatarUpload, setAvatarUpload] = useState([]);
  const [personalInfoForm, setPersonalInfoForm] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [isUrlParamProcessed, setIsUrlParamProcessed] = useState(false);
  const [showNeedsDetailsModal, setShowNeedsDetailsModal] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [open, setOpen] = useState({});
  const successToastRef = useRef();
  const errorToastRef = useRef();

  const handleOpen = (id) => {
    setOpen({ ...open, [id]: true });
  };

  const handleClose = (id) => {
    setOpen({ ...open, [id]: false });
  };

  useEffect(() => {
    if (queryParams.get('view') === 'settings' && !isUrlParamProcessed) {
      setTimeout(() => {
        setActiveTab(t('settings'));
        setIsUrlParamProcessed(true);
      }, 1000);

      setTimeout(() => {
        let url = new URL(window.location);
        url.searchParams.delete('view');
        window.history.replaceState({}, '', url);
      }, 0);
    }
  }, [isUrlParamProcessed]);

  useEffect(() => {
    if (location.state?.needsPhone && location.state?.needsCity) {
      setShowNeedsDetailsModal(true);
    }
  }, [location.state]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    // Create a copy of the current form state
    const updatedForm = { ...personalInfoForm };

    if (name === 'phoneNumber') {
      // Update the raw input value first
      updatedForm[name] = value;

      // Validate the phone number without adding +370
      if (!value.startsWith('6') || value.length !== 8 || isNaN(value)) {
        setPhoneError(
          'Įveskite teisingo formato telefono numerį, prasidedantį nuo 6, pvz: (612 34567)',
        );
      } else {
        setPhoneError('');
      }
    } else if (name === 'messenger') {
      let username = value;
      try {
        const url = new URL(value);
        username = url.pathname.substring(1); // Remove the leading slash
      } catch (_) {
        // Ignore invalid URLs
      }
      updatedForm[name] = `https://m.me/${username}`;
    } else {
      updatedForm[name] = value;
    }

    // Update the form state
    setPersonalInfoForm(updatedForm);
  };

  const getUserInfo = async () => {
    try {
      const response = await get(`/accounts/myprofile`);
      const data = await response.json();
      setUserInfo(data[0]);

      const rawPhoneNumber = data[0].phone_number?.startsWith('+370')
        ? data[0].phone_number.slice(4)
        : data[0].phone_number;

      setPersonalInfoForm({
        firstName: data[0].first_name,
        lastName: data[0].last_name,
        email: data[0].email,
        phoneNumber: rawPhoneNumber,
        messenger: data[0].messenger,
        city: data[0].city,
        ntrp: data[0].ntrp,
        image: data[0].image,
        description: data[0].description,
      });
      setSettingsForm({
        showEmail: data[0].showEmail,
        showPhone: data[0].showPhone,
        newsLettersEmail: data[0].newsletters,
        newsLettersPhone: data[0].newsletters_sms,
        password: '',
        repeatPassword: '',
        scheduledEmails: data[0].scheduledEmails,
      });
    } catch (err) {
      console.error(err.message);
    }
  };

  useEffect(() => {
    getUserInfo();
  }, []);

  const handleSubmit = async () => {
    if (activeTab === t('personalInfo')) {
      try {
        const formToSubmit = { ...personalInfoForm };

        if (avatarUpload.length > 0) {
          const formData = new FormData();
          formData.append('image', avatarUpload[0]);
          await upload(`/singleImage`, formData);
        }

        if (
          formToSubmit.phoneNumber &&
          !formToSubmit.phoneNumber.startsWith('+370')
        ) {
          formToSubmit.phoneNumber = `+370${formToSubmit.phoneNumber}`;
        }

        if (!formToSubmit.phoneNumber) {
          setShowNeedsDetailsModal(true);
          return;
        }

        if (!formToSubmit.city) {
          setShowNeedsDetailsModal(true);
          return;
        }

        const response = await put(`/accounts/myprofiles`, formToSubmit);
        const data = await response.json();
        if (data.status === 'SUCCESS') {
          successToastRef.current.showSuccess(data.msg);
          getUserInfo();
          window.scrollTo(0, 0);
        } else {
          errorToastRef.current.showError(data.err);
        }
      } catch (err) {
        console.error(err.message);
      }
    } else if (activeTab === t('settings')) {
      try {
        const response = await put(`/accounts/myprofiles`, settingsForm);
        const data = await response.json();
        if (data.status === 'SUCCESS') {
          successToastRef.current.showSuccess(data.msg);
          getUserInfo();
          window.scrollTo(0, 0);
        } else {
          errorToastRef.current.showError(data.err);
        }
      } catch (err) {
        console.error(err.message);
      }
    }
  };

  return (
    <Box width={'100%'} display={'flex'} flexDirection={'column'}>
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <Box>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              variant="h5"
              color={theme.palette.darkGrey[700]}
              fontWeight={700}
            >
              {t('personalInfoTitle')}
            </Typography>
            <Typography
              variant="body3"
              color={theme.palette.darkGrey[600]}
              marginTop={{ xs: '8px', sm: '0px' }}
            >
              {t('personalInfoSubtitle')}
            </Typography>
          </Box>
          <Box
            display={{ xs: 'none', sm: 'flex' }}
            alignItems={'center'}
            maxWidth={'220px'}
            width={'100%'}
            justifyContent={'flex-end'}
          >
            <Button
              bordered="true"
              buttontype="white"
              onClick={() => navigate('/info', { replace: true })}
              marginRight={'16px'}
            >
              {t('cancel')}
            </Button>
            <Button buttontype="orange" onClick={handleSubmit}>
              {t('save')}
            </Button>
          </Box>
        </Box>
        <Box
          backgroundColor={theme.palette.darkGrey[200]}
          height={'1px'}
          marginTop={'20px'}
        />
      </Box>
      <Box marginTop={'40px'} marginBottom={'40px'}>
        <Box
          display={'flex'}
          alignItems={'center'}
          maxWidth={'576px'}
          width={'100%'}
          sx={{
            borderBottom: `1px solid ${theme.palette.darkGrey[300]}`,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <SecondaryTab
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabName={t('personalInfo')}
            page={'profile'}
          />
          <SecondaryTab
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabName={t('settings')}
            page={'profile'}
          />
        </Box>
      </Box>
      <Box>
        {activeTab === t('personalInfo') && (
          <PersonalInfo
            personalInfoForm={personalInfoForm}
            avatarUpload={avatarUpload}
            setAvatarUpload={setAvatarUpload}
            handleChange={handleChange}
            phoneError={phoneError}
          />
        )}
        {activeTab === t('settings') && (
          <Settings
            settingsForm={settingsForm}
            setSettingsForm={setSettingsForm}
          />
        )}
      </Box>
      <Box
        backgroundColor={theme.palette.darkGrey[200]}
        height={'1px'}
        marginBottom={'20px'}
        marginTop={'20px'}
      />
      <Box width={'100%'} display={'flex'} justifyContent={'flex-end'}>
        <Box
          display={'flex'}
          alignItems={'center'}
          maxWidth={'220px'}
          width={'100%'}
          justifyContent={'flex-end'}
        >
          <Button
            bordered="true"
            buttontype="white"
            onClick={() => navigate('/info', { replace: true })}
            marginRight={'16px'}
          >
            {t('cancel')}
          </Button>
          <Button buttontype="orange" onClick={handleSubmit}>
            {t('save')}
          </Button>
        </Box>
      </Box>
      <Modal
        open={showNeedsDetailsModal}
        onClose={() => {}}
        aria-labelledby="phone-required-modal"
        disableEscapeKeyDown
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          bgcolor="white"
          p={4}
          borderRadius={2}
          boxShadow={4}
          width="90%"
          maxWidth={{ xs: '100%', sm: '30%' }}
        >
          <Typography variant="h6" mb={2}>
            🧾 {t('firstSignup')}
          </Typography>
          <Typography variant="body2" mb={2}>
            {t('firstSignupSubtitle')}
          </Typography>
          <Box
            display={'flex'}
            maxHeight={'126px'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Box display={'flex'} flexDirection={'column'} width={'100%'}>
              <Typography
                variant="body4"
                color={theme.palette.darkGrey[700]}
                fontWeight={600}
              >
                {t('firstName')}
              </Typography>
            </Box>
            <TextInput
              id="firstName"
              name="firstName"
              type="text"
              placeholder={t('firstName')}
              required
              autoComplete="given-name"
              handleInputChange={handleChange}
              value={personalInfoForm.firstName || ''}
              sx={{
                margin: 0,
                maxWidth: { xs: '100%', sm: '298px', md: '304px' },
                mt: { xs: '6px', sm: '0px' },
              }}
            />
          </Box>
          <Box
            display={'flex'}
            maxHeight={'126px'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
            flexDirection={{ xs: 'column', sm: 'row' }}
            marginTop={'16px'}
          >
            <Box display={'flex'} flexDirection={'column'} width={'100%'}>
              <Typography
                variant="body4"
                color={theme.palette.darkGrey[700]}
                fontWeight={600}
              >
                {t('lastName')}
              </Typography>
            </Box>
            <TextInput
              id="lastName"
              name="lastName"
              type="text"
              placeholder={t('lastName')}
              required
              autoComplete="family-name"
              handleInputChange={handleChange}
              value={personalInfoForm.lastName || ''}
              sx={{
                margin: 0,
                maxWidth: { xs: '100%', sm: '298px', md: '304px' },
                mt: { xs: '6px', sm: '0px' },
              }}
            />
          </Box>
          <Box
            display={'flex'}
            maxHeight={'126px'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
            flexDirection={{ xs: 'column', sm: 'row' }}
            marginTop={'16px'}
          >
            <Box display={'flex'} flexDirection={'column'} width={'100%'}>
              <Typography
                variant="body4"
                color={theme.palette.darkGrey[700]}
                fontWeight={600}
              >
                {t('mobileNumber')} (+370)
              </Typography>
            </Box>
            <TextInput
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              placeholder={t('enterPhone')}
              required
              autoComplete="tel"
              handleInputChange={handleChange}
              value={personalInfoForm.phoneNumber || ''}
              inputProps={{ maxLength: 8 }}
              helperText={phoneError}
              error={!!phoneError}
              sx={{
                margin: 0,
                maxWidth: { xs: '100%', sm: '298px', md: '304px' },
                mt: { xs: '6px', sm: '0px' },
              }}
            />
          </Box>
          <Box
            display={'flex'}
            maxHeight={'126px'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
            flexDirection={{ xs: 'column', sm: 'row' }}
            marginTop={'16px'}
          >
            <Box display={'flex'} flexDirection={'column'} width={'100%'}>
              <Typography
                variant="body4"
                color={theme.palette.darkGrey[700]}
                fontWeight={600}
              >
                {t('city')}
              </Typography>
            </Box>
            <Box
              width={'100%'}
              sx={{
                mt: { xs: '6px', sm: '0px' },
                maxWidth: { xs: '100%', sm: '298px', md: '304px' },
              }}
            >
              <SelectInput
                name="city"
                id="city"
                placeholder={t('selectCity')}
                open={open}
                handleOpen={handleOpen}
                handleClose={handleClose}
                handleChange={handleChange}
                value={personalInfoForm.city || ''}
                options={Cities.filter((option) => option.value !== '')}
              />
            </Box>
          </Box>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              disabled={
                !personalInfoForm.firstName ||
                !personalInfoForm.lastName ||
                !personalInfoForm.phoneNumber ||
                personalInfoForm.phoneNumber.length !== 8 ||
                !personalInfoForm.city
              }
              onClick={async () => {
                await handleSubmit();
                setShowNeedsDetailsModal(false);
                navigate('/redaguoti-profili', { replace: true });
              }}
              buttontype="orange"
            >
              Išsaugoti
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ContentView;
